import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ParsedData, QuestionScores, Entity, CustomTheme } from './types';
import { parseNERFile, parseNERContent } from './utils/fileParser';
import { exportToJSON, calculateModelSummaries } from './utils/exportUtils';
import { loadState, saveState, clearState } from './utils/storage';
import FileUpload from './components/FileUpload';
import ComparisonView from './components/ComparisonView';
// NewExampleCard removed in favor of inline separators
import ModelSummary from './components/ModelSummary';
import EntityReference from './components/EntityReference';
import SettingsDrawer from './components/SettingsDrawer';

function App() {
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const [scores, setScores] = useState<QuestionScores>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [labelFilters, setLabelFilters] = useState<Set<string>>(new Set());
  const [newExampleText, setNewExampleText] = useState<string>('');
  const [isEditingFileName, setIsEditingFileName] = useState<boolean>(false);
  const [fileNameDraft, setFileNameDraft] = useState<string>('');
  const fileNameInputRef = useRef<HTMLInputElement>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [allowAddExamples, setAllowAddExamples] = useState<boolean>(false);
  const [allowBatchLabelRenaming, setAllowBatchLabelRenaming] = useState<boolean>(true);

  // Load persisted state on mount
  useEffect(() => {
    const saved = loadState();
    if (saved) {
      setParsedData(saved.parsedData);
      setCurrentFileName(saved.currentFileName);
      setScores(saved.scores);
      setSearchTerm(saved.searchTerm || '');
      // Don't persist label filters to avoid confusion on reload
      // setLabelFilters(new Set(saved.labelFilters || []));
      setLabelFilters(new Set()); // Always start with no filters
      if (typeof saved.allowAddExamples === 'boolean') {
        setAllowAddExamples(saved.allowAddExamples);
      }
      if (typeof saved.allowBatchLabelRenaming === 'boolean') {
        setAllowBatchLabelRenaming(saved.allowBatchLabelRenaming);
      }
    }
  }, []);

  // Listen for add-example events dispatched by separators in the ComparisonView
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      handleAddExampleInternal(detail.text, detail.insertAfterId);
    };
    window.addEventListener('add-example', handler as EventListener);
    return () => window.removeEventListener('add-example', handler as EventListener);
  }, [parsedData]);

  // Autosave on state changes (but only if we have data loaded)
  useEffect(() => {
    if (parsedData) {
      saveState({
        parsedData,
        currentFileName,
        scores,
        searchTerm,
        labelFilters: Array.from(labelFilters),
        allowAddExamples,
        allowBatchLabelRenaming
      });
    }
  }, [parsedData, currentFileName, scores, searchTerm, labelFilters, allowAddExamples, allowBatchLabelRenaming]);

  const handleAddExampleInternal = useCallback((text: string, insertAfterId?: string | null) => {
    if (!parsedData) return;
    const value = text.trim();
    if (!value) return;

    const existingIds = new Set(parsedData.questions.map(q => q.id));
    let counter = parsedData.questions.length + 1;
    let newId = `q${counter}`;
    while (existingIds.has(newId)) { counter += 1; newId = `q${counter}`; }

    const newQuestion = { id: newId, text: value, modelResponses: [], humanAnnotations: [] as Entity[] };

    setParsedData(prev => {
      if (!prev) return prev;
      if (!insertAfterId) {
        return { ...prev, questions: [newQuestion, ...prev.questions] };
      }
      const idx = prev.questions.findIndex(q => q.id === insertAfterId);
      if (idx === -1) return { ...prev, questions: [...prev.questions, newQuestion] };
      const before = prev.questions.slice(0, idx + 1);
      const after = prev.questions.slice(idx + 1);
      return { ...prev, questions: [...before, newQuestion, ...after] };
    });
  }, [parsedData]);

  // Focus filename input when entering edit mode
  useEffect(() => {
    if (isEditingFileName) {
      const id = requestAnimationFrame(() => fileNameInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [isEditingFileName]);

  const startEditFileName = useCallback(() => {
    setFileNameDraft(currentFileName || 'Untitled.json');
    setIsEditingFileName(true);
  }, [currentFileName]);

  const commitEditFileName = useCallback(() => {
    const next = fileNameDraft.trim();
    if (next && next !== currentFileName) {
      setCurrentFileName(next);
    }
    setIsEditingFileName(false);
  }, [fileNameDraft, currentFileName]);

  const cancelEditFileName = useCallback(() => {
    setIsEditingFileName(false);
    setFileNameDraft('');
  }, []);

  const handleFileUpload = useCallback((file: File, content: string) => {
    try {
      console.log('Uploading file:', file.name);
      console.log('Content preview:', content.substring(0, 200));
      
      const data = parseNERContent(content, file.name);
      
      console.log('Parsed data:', data);
      console.log('Questions found:', data.questions.length);
      console.log('Model names:', data.modelNames);
      
      // Allow empty projects (0 questions) to load
      
      setParsedData(data);
      setCurrentFileName(file.name);
      setScores({});
      setSearchTerm('');
      setLabelFilters(new Set());
      setAllowBatchLabelRenaming(true); // Default to enabled
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error parsing file. Please check the format.\n\nError: ' + (error as Error).message);
    }
  }, []);

  const handleClear = useCallback(() => {
    setParsedData(null);
    setCurrentFileName('');
    setScores({});
    setSearchTerm('');
    setLabelFilters(new Set());
    clearState();
  }, []);

  const handleScoreUpdate = useCallback((questionId: string, modelName: string, score: any) => {
    setScores(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [modelName]: score
      }
    }));
  }, []);

  const handleAnnotationsUpdate = useCallback((questionId: string, annotations: Entity[]) => {
    setParsedData(prev => {
      if (!prev) return prev;
      
      return {
        ...prev,
        questions: prev.questions.map(q => 
          q.id === questionId 
            ? { ...q, humanAnnotations: annotations }
            : q
        )
      };
    });
  }, []);

  const handleBatchLabelEdit = useCallback((oldLabel: string, newLabel: string, newColor: string) => {
    if (!parsedData || !newLabel.trim()) return;
    
    setParsedData(prev => {
      if (!prev) return prev;
      
      const updatedCustomColors = { ...(prev.customLabelColors || {}) };
      
      // If label name changed, remove old color mapping
      if (oldLabel !== newLabel.trim() && updatedCustomColors[oldLabel]) {
        delete updatedCustomColors[oldLabel];
      }
      
      // Set new color for the label
      updatedCustomColors[newLabel.trim()] = newColor;
      
      return {
        ...prev,
        customLabelColors: updatedCustomColors,
        questions: prev.questions.map(question => ({
          ...question,
          // Update entities in model responses
          modelResponses: question.modelResponses.map(response => ({
            ...response,
            entities: response.entities.map(entity =>
              entity.label === oldLabel
                ? { ...entity, label: newLabel.trim() }
                : entity
            )
          })),
          // Update human annotations
          humanAnnotations: question.humanAnnotations?.map(entity =>
            entity.label === oldLabel
              ? { ...entity, label: newLabel.trim() }
              : entity
          )
        }))
      };
    });
  }, [parsedData]);

  const handleSaveTheme = useCallback((theme: Omit<CustomTheme, 'id'>) => {
    setParsedData(prev => {
      if (!prev) return prev;
      
      const savedThemes = prev.savedThemes || [];
      
      // Check if theme already exists (same colors)
      const existingTheme = savedThemes.find(
        t => t.bgColor === theme.bgColor && 
             t.textColor === theme.textColor && 
             t.borderColor === theme.borderColor
      );
      
      if (existingTheme) {
        // Theme already exists, don't add duplicate
        return prev;
      }
      
      // Create new theme with unique ID
      const newTheme: CustomTheme = {
        ...theme,
        id: `theme-${Date.now()}-${Math.random().toString(36).substring(7)}`
      };
      
      return {
        ...prev,
        savedThemes: [...savedThemes, newTheme]
      };
    });
  }, []);

  const handleDeleteLabel = useCallback((label: string, alsoDeleteLabelDefinition: boolean) => {
    setParsedData(prev => {
      if (!prev) return prev;
      
      const updatedCustomColors = { ...(prev.customLabelColors || {}) };
      
      // Remove label from customLabelColors if requested
      // This ensures the label won't appear in the available labels list
      if (alsoDeleteLabelDefinition) {
        delete updatedCustomColors[label];
      }
      
      return {
        ...prev,
        customLabelColors: updatedCustomColors,
        questions: prev.questions.map(question => ({
          ...question,
          // Remove entities from model responses
          modelResponses: question.modelResponses.map(response => ({
            ...response,
            entities: response.entities.filter(entity => entity.label !== label)
          })),
          // Remove from human annotations
          humanAnnotations: question.humanAnnotations?.filter(entity => entity.label !== label)
        }))
      };
    });
  }, []);

  const handleMergeLabel = useCallback((fromLabel: string, toLabel: string) => {
    setParsedData(prev => {
      if (!prev) return prev;
      
      const updatedCustomColors = { ...(prev.customLabelColors || {}) };
      
      // Remove custom color for the old label
      if (updatedCustomColors[fromLabel]) {
        delete updatedCustomColors[fromLabel];
      }
      
      return {
        ...prev,
        customLabelColors: updatedCustomColors,
        questions: prev.questions.map(question => ({
          ...question,
          // Update entities in model responses
          modelResponses: question.modelResponses.map(response => ({
            ...response,
            entities: response.entities.map(entity =>
              entity.label === fromLabel
                ? { ...entity, label: toLabel }
                : entity
            )
          })),
          // Update human annotations
          humanAnnotations: question.humanAnnotations?.map(entity =>
            entity.label === fromLabel
              ? { ...entity, label: toLabel }
              : entity
          )
        }))
      };
    });
  }, []);

  const handleCreateLabel = useCallback((label: string, color: string) => {
    setParsedData(prev => {
      if (!prev) return prev;
      
      const updatedCustomColors = { ...(prev.customLabelColors || {}) };
      updatedCustomColors[label] = color;
      
      return {
        ...prev,
        customLabelColors: updatedCustomColors
      };
    });
  }, []);

  const handleExportJSON = useCallback(() => {
    if (!parsedData) return;
    
    const jsonData = exportToJSON(parsedData, scores);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ensuredName = currentFileName && currentFileName.toLowerCase().endsWith('.json')
      ? currentFileName
      : `${currentFileName.replace(/\.[^/.]+$/, '')}.json`;
    a.download = ensuredName || 'export.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [parsedData, scores, currentFileName]);

  

  const handleLabelFilter = useCallback((label: string, modelName: string | null) => {
    const filterKey = modelName ? `${modelName}:${label}` : label;
    
    setLabelFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(filterKey)) {
        newFilters.delete(filterKey);
      } else {
        newFilters.add(filterKey);
      }
      return newFilters;
    });
  }, []);

  const filteredQuestions = parsedData?.questions.filter(question => {
    // Text search filter
    const matchesSearch = question.text.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Label filter
    if (labelFilters.size === 0) {
      return matchesSearch;
    }
    
    // Check if question has any model response or human annotation that matches any active filter
    const matchesLabelFilter = Array.from(labelFilters).some(filterKey => {
      const [modelName, label] = filterKey.includes(':') 
        ? filterKey.split(':')
        : [null, filterKey];
      
      // Check model responses
      const matchesModelResponse = question.modelResponses.some(response => {
        // If filter specifies a model, check that model
        if (modelName && response.modelName !== modelName) {
          return false;
        }
        
        // Check if this response has the label
        return response.entities.some(entity => entity.label === label);
      });
      
      // Also check human annotations if no model filter specified
      const matchesHumanAnnotation = !modelName && question.humanAnnotations?.some(entity => 
        entity.label === label
      );
      
      return matchesModelResponse || matchesHumanAnnotation;
    });
    
    return matchesSearch && matchesLabelFilter;
  }) || [];

  const modelSummaries = parsedData ? calculateModelSummaries(parsedData, scores) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div>
                <a 
                  href="https://softwaresushi.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity block"
                >
                  <img
                    src="/logo.png"
                    alt="NER Tool logo"
                    className="h-8 w-50 object-contain cursor-pointer"
                  />
                </a>
                <h1 className="text-2xl font-bold text-gray-900">NER Tool</h1>
                {currentFileName && (
                  <div className="text-sm text-gray-600 mt-1 flex items-center space-x-1">
                    <span className="text-gray-500">File:</span>
                    {!isEditingFileName ? (
                      <button
                        onClick={startEditFileName}
                        className="underline decoration-dotted hover:decoration-solid hover:text-gray-900"
                        title="Click to rename"
                      >
                        {currentFileName}
                      </button>
                    ) : (
                      <input
                        ref={fileNameInputRef}
                        type="text"
                        value={fileNameDraft}
                        onChange={(e) => setFileNameDraft(e.target.value)}
                        onBlur={commitEditFileName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitEditFileName(); }
                          else if (e.key === 'Escape') { e.preventDefault(); cancelEditFileName(); }
                        }}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-4 items-center">
              {parsedData && (
                <>
                  <button
                    onClick={handleExportJSON}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Export JSON
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Clear
                  </button>
                </>
              )}
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                title="Settings"
                aria-label="Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!parsedData ? (
          <FileUpload
            onFileUpload={handleFileUpload}
            onStartNew={() => {
              // Initialize empty project
              setParsedData({ questions: [], modelNames: [] });
              setCurrentFileName('Untitled.json');
              setAllowAddExamples(true); // default enabled for new project
              setAllowBatchLabelRenaming(true); // default enabled for new project
            }}
          />
        ) : (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  {labelFilters.size > 0 && (
                    <button
                      onClick={() => setLabelFilters(new Set())}
                      className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    >
                      Clear Filters ({labelFilters.size})
                    </button>
                  )}
                  <div className="text-sm text-gray-600">
                    {filteredQuestions.length} of {parsedData.questions.length} questions
                  </div>
                </div>
              </div>
              {/* Inline add example input removed; separators handle creation */}
            </div>

            {/* Debug Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <strong>Debug Info:</strong> {parsedData.questions.length} questions loaded, {parsedData.modelNames.length} models ({parsedData.modelNames.join(', ')})
            </div>

            {/* Entity Reference */}
            <EntityReference 
              data={parsedData} 
              onLabelFilter={handleLabelFilter}
              activeFilters={labelFilters}
              allowBatchLabelRenaming={allowBatchLabelRenaming}
              onBatchLabelEdit={handleBatchLabelEdit}
              onSaveTheme={handleSaveTheme}
              onDeleteLabel={handleDeleteLabel}
              onMergeLabel={handleMergeLabel}
              onCreateLabel={handleCreateLabel}
            />

            {/* Model Summary */}
            {modelSummaries.length > 0 && (
              <ModelSummary summaries={modelSummaries} />
            )}

            {/* Main Content */}
            <ComparisonView
              questions={filteredQuestions}
              modelNames={parsedData.modelNames}
              scores={scores}
              onScoreUpdate={handleScoreUpdate}
              onAnnotationsUpdate={handleAnnotationsUpdate}
              allowAddExamples={allowAddExamples}
              customLabelColors={parsedData.customLabelColors}
            />
            
          </div>
        )}
      </div>

      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        allowAddExamples={allowAddExamples}
        onToggleAllowAddExamples={(next) => setAllowAddExamples(next)}
        allowBatchLabelRenaming={allowBatchLabelRenaming}
        onToggleAllowBatchLabelRenaming={(next) => setAllowBatchLabelRenaming(next)}
      />
    </div>
  );
}

export default App;
